import { Fragment, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { JsonBlock } from "@/components/ui/json-block";
import { Checkbox } from "@/components/ui/checkbox";
import { formatRelativeTime, truncate } from "@/lib/utils";
import type { ValidationIssue } from "@/types/validation";

interface IssuesTableProps {
  issues: ValidationIssue[];
  isLoading?: boolean;
  onResolve: (issue: ValidationIssue) => void;
  onViewDetails: (issue: ValidationIssue) => void;
  onScrapeUrl: (url: string) => void;
  onCreateOverride?: (issue: ValidationIssue) => void;
  /**
   * Optional controlled state for expanded rows
   * If provided, the component will use this instead of internal state
   */
  expandedRows?: Set<string>;
  onExpandedRowsChange?: (rows: Set<string>) => void;
  /**
   * Optional controlled selection state for checkbox selection
   * If provided, the component will use this instead of internal state
   */
  selectedIssueIds?: Set<number>;
  onSelectedIssueIdsChange?: (ids: Set<number>) => void;
}

export function IssuesTable({
  issues,
  isLoading,
  onResolve,
  onViewDetails,
  onScrapeUrl,
  onCreateOverride,
  expandedRows: controlledExpandedRows,
  onExpandedRowsChange,
  selectedIssueIds: controlledSelectedIssueIds,
  onSelectedIssueIdsChange,
}: IssuesTableProps) {
  // Internal state (used only if controlled props not provided)
  const [internalExpandedRows, setInternalExpandedRows] = useState<Set<string>>(
    new Set(),
  );
  const [internalSelectedIssueIds, setInternalSelectedIssueIds] = useState<
    Set<number>
  >(new Set());

  // Use controlled state if provided, otherwise use internal state
  const expandedRows = controlledExpandedRows ?? internalExpandedRows;
  const setExpandedRows = onExpandedRowsChange ?? setInternalExpandedRows;

  const selectedIssueIds =
    controlledSelectedIssueIds ?? internalSelectedIssueIds;
  const setSelectedIssueIds =
    onSelectedIssueIdsChange ?? setInternalSelectedIssueIds;

  const toggleRow = (id: number) => {
    const key = String(id);
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedRows(newExpanded);
  };

  const isExpanded = (id: number) => expandedRows.has(String(id));

  const toggleSelected = (id: number) => {
    const next = new Set(selectedIssueIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIssueIds(next);
  };

  const unresolvedIssues = issues.filter(
    (issue) => issue.status === "unresolved",
  );
  const unresolvedIds = new Set(unresolvedIssues.map((i) => i.id));
  const selectedUnresolvedCount = Array.from(selectedIssueIds).filter((id) =>
    unresolvedIds.has(id),
  ).length;
  const allUnresolvedSelected =
    unresolvedIssues.length > 0 &&
    selectedUnresolvedCount === unresolvedIssues.length;
  const someUnresolvedSelected =
    selectedUnresolvedCount > 0 && !allUnresolvedSelected;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (issues.length === 0) {
    return (
      <div className="text-center py-12 border border-border rounded-lg">
        <p className="text-muted-foreground">No validation issues found</p>
        <p className="text-sm text-muted-foreground mt-2">
          Try adjusting your filters
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">
              {issues.length > 0 && (
                <Checkbox
                  checked={
                    allUnresolvedSelected
                      ? true
                      : someUnresolvedSelected
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(checked) => {
                    const next = new Set(selectedIssueIds);
                    if (checked === true) {
                      unresolvedIssues.forEach((issue) => next.add(issue.id));
                    } else {
                      unresolvedIssues.forEach((issue) =>
                        next.delete(issue.id),
                      );
                    }
                    setSelectedIssueIds(next);
                  }}
                  aria-label="Select all unresolved issues on this page"
                />
              )}
            </TableHead>
            <TableHead className="w-8" />
            <TableHead className="w-20">Level</TableHead>
            <TableHead className="w-32">Type</TableHead>
            <TableHead className="w-28">Field</TableHead>
            <TableHead className="w-24">Source</TableHead>
            <TableHead className="w-24">Scraper</TableHead>
            <TableHead className="w-24">Scope</TableHead>
            <TableHead className="w-64">URL</TableHead>
            <TableHead>Message</TableHead>
            <TableHead className="w-24">Status</TableHead>
            <TableHead className="w-32">Scraped</TableHead>
            <TableHead className="w-48 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {issues.map((issue) => {
            const expanded = isExpanded(issue.id);
            const rowKey = String(issue.id);
            const isSelected = selectedIssueIds.has(issue.id);
            return (
              <Fragment key={rowKey}>
                <TableRow className="cursor-pointer">
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelected(issue.id)}
                      aria-label={`Select issue ${issue.id}`}
                    />
                  </TableCell>
                  <TableCell onClick={() => toggleRow(issue.id)}>
                    {expanded ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        issue.level === "error" ? "destructive" : "secondary"
                      }
                    >
                      {issue.level}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {truncate(issue.validation_type, 20)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {issue.field_name ? truncate(issue.field_name, 24) : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="uppercase text-xs">
                      {issue.source}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {issue.scraper_source ? (
                      <Badge variant="outline" className="uppercase text-xs">
                        {issue.scraper_source}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize text-xs">
                      {issue.scope}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <a
                      href={issue.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:opacity-90 text-sm flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {truncate(issue.url, 40)}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </TableCell>
                  <TableCell className="text-sm text-foreground">
                    {truncate(issue.message, expanded ? 1000 : 60)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        issue.status === "addressed" ? "default" : "secondary"
                      }
                    >
                      {issue.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatRelativeTime(issue.scraped_at)}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-2">
                      {issue.status === "unresolved" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onResolve(issue)}
                        >
                          Resolve
                        </Button>
                      )}
                      {onCreateOverride &&
                        issue.external_variant_id &&
                        issue.scraper_source && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onCreateOverride(issue)}
                          >
                            Override
                          </Button>
                        )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onViewDetails(issue)}
                      >
                        Details
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onScrapeUrl(issue.url)}
                      >
                        Scrape
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expanded && (
                  <TableRow>
                    <TableCell colSpan={13} className="bg-muted/50">
                      <div className="p-4 space-y-2">
                        <div>
                          <span className="text-sm font-medium text-muted-foreground">
                            Full Message:
                          </span>
                          <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">
                            {issue.message}
                          </p>
                        </div>
                        {issue.product_id && (
                          <div>
                            <span className="text-sm font-medium text-muted-foreground">
                              Product ID:
                            </span>
                            <p className="text-sm text-foreground mt-1">
                              {issue.product_id}
                            </p>
                          </div>
                        )}
                        {issue.variant_id && (
                          <div>
                            <span className="text-sm font-medium text-muted-foreground">
                              Variant ID:
                            </span>
                            <p className="text-sm text-foreground mt-1">
                              {issue.variant_id}
                            </p>
                          </div>
                        )}
                        {issue.external_variant_id && (
                          <div>
                            <span className="text-sm font-medium text-muted-foreground">
                              External Variant ID:
                            </span>
                            <p className="text-sm text-foreground mt-1">
                              {issue.external_variant_id}
                            </p>
                          </div>
                        )}
                        {issue.details &&
                          Object.keys(issue.details).length > 0 && (
                            <div>
                              <span className="text-sm font-medium text-muted-foreground">
                                Details:
                              </span>
                              <JsonBlock
                                value={issue.details}
                                className="text-sm text-foreground mt-1 bg-background p-2 rounded overflow-x-auto"
                              />
                            </div>
                          )}
                        {issue.status === "addressed" && (
                          <div className="pt-2 border-t border-border">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <span className="text-sm font-medium text-muted-foreground">
                                  Resolution Type:
                                </span>
                                <p className="text-sm text-foreground mt-1 capitalize">
                                  {issue.resolution_type?.replace("_", " ")}
                                </p>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-muted-foreground">
                                  Addressed At:
                                </span>
                                <p className="text-sm text-foreground mt-1">
                                  {issue.addressed_at
                                    ? formatRelativeTime(issue.addressed_at)
                                    : "N/A"}
                                </p>
                              </div>
                            </div>
                            {issue.resolution_notes && (
                              <div className="mt-2">
                                <span className="text-sm font-medium text-muted-foreground">
                                  Resolution Notes:
                                </span>
                                <p className="text-sm text-foreground mt-1">
                                  {issue.resolution_notes}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
